/**
 * Collection cycles — SPEC §3.4.
 *
 * A cycle is named for the month being collected FOR, always one month ahead of the
 * current month: during January the portal collects February, so the required cycle is
 * `2026-02` and its deadline is January 10th.
 *
 * Everything here is derived from a calendar date rather than stored, so there is no cycle
 * table to fall out of step with the calendar and no rollover job to forget to run.
 */
import { addMonths, compare, cycleKeyOf, daysInMonth, differenceInDays, parseCycleKey, toCycleKey, } from '../dates/calendar.js';
/** Availability for the required month is due by this day of the preceding month. */
export const DEADLINE_DAY = 10;
/** How many months the portal shows: the required one, plus two to plan ahead. */
export const DISPLAY_MONTHS = 3;
/** The cycle currently being collected. In January this is February. */
export function requiredCycle(today) {
    return addMonths(cycleKeyOf(today), 1);
}
/** The month during which a cycle is collected — one before the cycle's own month. */
export function collectionMonthOf(cycle) {
    return addMonths(cycle, -1);
}
/** The 10th of the collection month. Availability for `cycle` is due by end of this day. */
export function deadlineFor(cycle) {
    const { year, month } = parseCycleKey(collectionMonthOf(cycle));
    return { year, month, day: DEADLINE_DAY };
}
/** First day of a cycle's month. */
export function firstDayOf(cycle) {
    const { year, month } = parseCycleKey(cycle);
    return { year, month, day: 1 };
}
/** Last day of a cycle's month. Leap-year aware. */
export function lastDayOf(cycle) {
    const { year, month } = parseCycleKey(cycle);
    return { year, month, day: daysInMonth(year, month) };
}
/**
 * The months to show, required first (SPEC §3.4).
 *
 * Only the first drives reminders and status; the other two exist so instructors who know
 * their schedule further out can submit early.
 */
export function displayCycles(today, count = DISPLAY_MONTHS) {
    const required = requiredCycle(today);
    return Array.from({ length: count }, (_, i) => addMonths(required, i));
}
/** Whether `cycle` is the one being collected right now. */
export function isRequired(cycle, today) {
    return cycle === requiredCycle(today);
}
/** Past the 10th and still owing. The form never closes; this only affects tone (SPEC §3.4). */
export function isPastDeadline(cycle, today) {
    return compare(today, deadlineFor(cycle)) > 0;
}
/** Negative once the deadline has passed. */
export function daysUntilDeadline(cycle, today) {
    return differenceInDays(deadlineFor(cycle), today);
}
/** `2026-03` → `March 2026`. For UI and email subject lines — never a hardcoded month name. */
export function formatCycle(cycle, locale = 'en-US') {
    const { year, month } = parseCycleKey(cycle);
    const name = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 1)));
    return `${name} ${year}`;
}
/** `2026-03-10` → `March 10, 2026`. Used for `{deadline_date}` in email copy (SPEC §13). */
export function formatDate(date, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(date.year, date.month - 1, date.day)));
}
/** Cycle a date belongs to. */
export function cycleOf(date) {
    return toCycleKey(date.year, date.month);
}
//# sourceMappingURL=cycle.js.map